import { createSelector } from '@reduxjs/toolkit';

import { LAYOUT_MODEL_SYNCHRONIZED_ACTIONS } from '@paragon/layout-models';
import { getLoggedInUserOrganization, shouldSynchronizeComponent } from '@paragon/login-tools';
import { makeIdSafe, SyncConfiguration } from '@paragon/sync-client';

import { RootState } from '..';
import { cameraSlice } from '../slices/camera/cameraSlice';
import { setAnalogJoint, setBearing, upsertPlatePairs } from '../slices/componentDesignsSlice';
import { loadStation } from '../slices/stationsSlice';
import { unsynchronizedChangeMade } from '../synchronizationActions';
import { getActiveComponentDesignGuid } from './getActiveComponentDesignSelector';

const getStationSyncConfiguration = createSelector(
  (state: RootState) => state.application.openContext,
  (context): SyncConfiguration[] => {
    if (context?.type !== 'station' && context?.type !== 'schedule-station') return [];

    return [
      {
        room: `#station-${context.stationGuid}`,
        synchronizedActions: [loadStation.type],
      },
    ];
  },
);

const getLayoutModelSyncConfiguration = createSelector(
  (state: RootState) => state.application.openContext,
  getLoggedInUserOrganization,
  (context, organization): SyncConfiguration[] => {
    if (context?.type !== 'project' || !organization) return [];
    return [
      {
        room: `#projects-${makeIdSafe(organization)}`,
        synchronizedActions: [...LAYOUT_MODEL_SYNCHRONIZED_ACTIONS, unsynchronizedChangeMade.type],
      },
    ];
  },
);

const getCameraSyncConfiguration = createSelector(
  (state: RootState) => state.visualOptions.synchronizeCamera,
  getActiveComponentDesignGuid,
  (shouldSyncCamera, componentDesignGuid): SyncConfiguration[] => {
    if (!shouldSyncCamera || !componentDesignGuid) return [];
    return [
      {
        room: `#design-${componentDesignGuid}`,
        synchronizedActions: [
          cameraSlice.actions.activeCameraChanged.type,
          cameraSlice.actions.cameraAltered.type,
          cameraSlice.actions.designCameraFlipped.type,
          cameraSlice.actions.designCameraReversed.type,
        ],
      },
    ];
  },
);

const getComponentDesignSyncConfiguration = createSelector(
  shouldSynchronizeComponent,
  getActiveComponentDesignGuid,
  (shouldSyncComponent, componentDesignGuid): SyncConfiguration[] => {
    if (!shouldSyncComponent || !componentDesignGuid) return [];

    return [
      {
        room: `#design-${componentDesignGuid}`,
        synchronizedActions: [upsertPlatePairs.type, setBearing.type, setAnalogJoint.type],
      },
    ];
  },
);

export const getSyncConfigurations = createSelector(
  getStationSyncConfiguration,
  getLayoutModelSyncConfiguration,
  getCameraSyncConfiguration,
  getComponentDesignSyncConfiguration,
  (
    stationSyncConfiguration,
    layoutModelSyncConfiguration,
    cameraSyncConfiguration,
    componentDesignSyncConfiguration,
  ): SyncConfiguration[] => [
    ...stationSyncConfiguration,
    ...layoutModelSyncConfiguration,
    ...cameraSyncConfiguration,
    ...componentDesignSyncConfiguration,
  ],
);
